import { DTDeviceType } from '@core/referential/dt-device-type.enum'

export enum DTEventType {
  TOUCH = 'touch',
  TEMPERATURE = 'temperature',
  OBJECT_PRESENT = 'objectPresent',
  WATER_PRESENT = 'waterPresent',
  MOTION = 'motion',
  CONNECTION_STATUS = 'connectionStatus',
  ETHERNET_STATUS = 'ethernetStatus',
  BATTERY_STATUS = 'batteryStatus',
  BATTERY = 'battery',
  NETWORK_STATUS = 'networkStatus',
  CELLULAR_STATUS = 'cellularStatus',
  WATER_MOVEMENT = 'waterMovement',
  WATER_MOVEMENT_TEMPERATURE = 'waterMovementTemperature',
  CO2 = 'co2',
  HUMIDITY = 'humidity',
  WATER_FLOW = 'waterFlow',
  WATER = 'water',
  GAS = 'gas',
  ELECTRICITY = 'electricity',
  FUEL = 'fuel',
  GAS_METER = 'gasMeter',
  WATER_METER = 'waterMeter',
  ELECTRICITY_METER = 'electricityMeter',
  PHOTOVOLTAIC_METER = 'photovoltaicMeter',
  THERMAL_METER = 'thermalMeter',
  FUEL_METER = 'fuelMeter',
  WATER_METER_CONSUMPTION = 'waterMeterConsumption',
  GAS_METER_CONSUMPTION = 'gasMeterConsumption',
  GAS_METER_CONSUMPTION_KWH = 'gasMeterConsumptionKwh',
  ELECTRICITY_METER_CONSUMPTION = 'electricityMeterConsumption',
  // Photovoltaic energy PRODUCED over the bucket, in kWh. The `Consumption`
  // suffix is not a mistake: every meter reader derives its series name as
  // `${deviceType}Consumption` (calculate-index.ts formatMeterData, and the
  // device/data-energy lambda), so a `...Production` series would simply never
  // be read. The UI labels it "Production".
  PHOTOVOLTAIC_METER_CONSUMPTION = 'photovoltaicMeterConsumption',
  THERMAL_METER_CONSUMPTION = 'thermalMeterConsumption',
  FUEL_METER_CONSUMPTION = 'fuelMeterConsumption',
  FUEL_METER_CONSUMPTION_KWH = 'fuelMeterConsumptionKwh',
  ENERGY = 'energy',
  OTHER_ENERGY = 'otherEnergy',
  DEFAULT = 'default',
  // Authorization series for AUTHORIZED_OFF points — 1 = autorisé, 0 = non
  // autorisé, derived from the point's writeLevel. Kept separate from DEFAULT
  // (which holds the on/off read state) so read and write never collide.
  WRITE_VALUE = 'writeValue',
  DISPOSABLE_BATTERY_VOLTAGE = 'disposable_battery_voltage',
  ELECTRICITY_POWER = 'electricityPower',
  PRESSURE = 'pressure',
  CHLORINE = 'chlorine',
  PH = 'ph',
  PERCENTAGE = 'percentage',
  TVOC = 'tvoc',
  // Durations in seconds (boiler min work/stop times) — kept out of the
  // temperature catch-all so the series is not mislabeled.
  DURATION = 'duration',
  // Débits d'air CTA en m³/h (flowCtaBlowing / flowCtaRecovery) — distinct de
  // WATER_FLOW et hors du catch-all temperature.
  AIR_FLOW = 'airFlow',
  // Niveau de luminosité des capteurs d'ambiance Milesight (AM307) : un INDICE
  // 0-5 rapporté par le capteur, pas des lux — d'où le nom « level ».
  LIGHT_LEVEL = 'lightLevel',
  // Éclairement en LUX des sondes de luminosité extérieure hypervision
  // (outdoorIlluminance, kind Numeric / unit lx côté Metabos). Distinct de
  // LIGHT_LEVEL ci-dessus, qui est un indice 0-5 sans unité physique.
  ILLUMINANCE = 'illuminance',
  // Points d'un compteur de chaleur de circuit (Tag_Hypervision (7), onglet « Circuit régulé ») :
  // puissance thermique instantanée en W, débit en L/h et volume cumulé en L. Trois séries dédiées
  // pour ne pas retomber dans le catch-all temperature ni dans WATER_FLOW (vitesse d'eau DT, m/s).
  POWER = 'power',
  FLOW_RATE = 'flowRate',
  // `volume` est l'index (DeviceType.VOLUME_METER), `volumeConsumption` le delta entre deux relevés, écrit par
  // la branche compteur de l'ingestion comme `waterMeter` / `waterMeterConsumption`. Idem `energy` (ci-dessus,
  // DeviceType.ENERGY_METER) / `energyConsumption`.
  VOLUME = 'volume',
  VOLUME_CONSUMPTION = 'volumeConsumption',
  ENERGY_CONSUMPTION = 'energyConsumption',
}

export const CoreData = [
  DTEventType.TOUCH,
  DTEventType.TEMPERATURE,
  DTEventType.OBJECT_PRESENT,
  DTEventType.WATER_PRESENT,
  DTEventType.MOTION,
  DTDeviceType.HUMIDITY,
  DTEventType.WATER_MOVEMENT,
  DTEventType.WATER_MOVEMENT_TEMPERATURE,
  DTEventType.CO2,
  DTEventType.WATER_FLOW,
  DTEventType.WATER,
  DTEventType.GAS,
  DTEventType.ELECTRICITY,
  DTEventType.FUEL,
  DTEventType.ENERGY,
  DTEventType.OTHER_ENERGY,
] as Array<string>
export const NonCoreData = [
  DTEventType.CONNECTION_STATUS,
  DTEventType.ETHERNET_STATUS,
  DTEventType.BATTERY_STATUS,
  DTEventType.NETWORK_STATUS,
  DTEventType.CELLULAR_STATUS,
  DTEventType.BATTERY,
  DTEventType.DISPOSABLE_BATTERY_VOLTAGE,
] as Array<string>

// Connector-only non-core events: they describe the cloud connector's own uplink
// (ethernet / cellular / connection state) and must only ever be written onto a
// gateway device row, never onto a sensor — otherwise they leave orphan
// cloudConnector* fields that nothing ever clears.
export const CloudConnectorData = [DTEventType.CONNECTION_STATUS, DTEventType.ETHERNET_STATUS, DTEventType.CELLULAR_STATUS] as Array<string>
